<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from 'vue';
export interface SearchResult {
  id: string | number;
  type: 'preorder' | 'merch';
  title: string;
  subtitle?: string;
  imageUrl?: string;
  imageLowQuality?: string;
}
interface Props {
  placeholder?: string;
  debounce?: number;
  results?: SearchResult[];
  loading?: boolean;
}
const props = withDefaults(defineProps<Props>(), {
  placeholder: 'Placeholder',
  debounce: 200,
  results: () => [],
  loading: false,
});
const emit = defineEmits<{
  search: [query: string];
  select: [result: SearchResult];
  clear: [];
}>();
const debouncing = ref(false);
const query = ref('');
const isOpen = ref(false);
const isFocused = ref(false);
const activeIndex = ref(-1);
const wrapperRef = ref<HTMLElement | null>(null);
const inputRef = ref<HTMLInputElement | null>(null);
let debounceTimer: ReturnType<typeof setTimeout>;
const badgeLabels: Record<string, string> = {
  preorder: 'Предзаказ',
  merch: 'Мерч',
};
const grouped = computed(() => {
  const order: SearchResult['type'][] = ['preorder', 'merch'];
  const map: Partial<Record<SearchResult['type'], SearchResult[]>> = {};
  props.results.forEach(r => {
    if (!map[r.type]) map[r.type] = [];
    map[r.type]!.push(r);
  });
  return order.filter(t => map[t]).map(t => ({ type: t, items: map[t]! }));
});
const flatResults = computed(() =>
  grouped.value.flatMap(g => g.items)
);
const showDropdown = computed(() =>
  isOpen.value && query.value.trim().length > 0
);
function getImage(item: SearchResult) {
  const API_URL = window.APP_CONFIG.API_URL;
  const defaultImage = API_URL + window.APP_CONFIG.DEFAULT_IMAGE;
  return item.imageUrl ? `${API_URL}/images/merch/${item.imageUrl}` : defaultImage;
}
function highlight(text: string) {
  const q = query.value.trim();
  if (!q) return text;
  const re = new RegExp(`(${q.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
  return text.replace(re, '<mark>$1</mark>');
}
function onBoxClick() {
  inputRef.value?.focus();
}
function onInput() {
  debouncing.value = true;
  activeIndex.value = -1;
  isOpen.value = true;
  clearTimeout(debounceTimer);
  debounceTimer = setTimeout(() => {
    emit('search', query.value);
    debouncing.value = false;
  }, props.debounce);
}
function onFocus() {
  isFocused.value = true;
  if (query.value.trim()) isOpen.value = true;
}
function onBlur() {
  isFocused.value = false;
  setTimeout(() => { isOpen.value = false; }, 150);
}
function onKeydown(e: KeyboardEvent) {
  const max = flatResults.value.length - 1;
  if (e.key === 'ArrowDown') {
    e.preventDefault();
    activeIndex.value = Math.min(activeIndex.value + 1, max);
  } else if (e.key === 'ArrowUp') {
    e.preventDefault();
    activeIndex.value = Math.max(activeIndex.value - 1, 0);
  } else if (e.key === 'Enter' && activeIndex.value >= 0) {
    select(flatResults.value[activeIndex.value]);
  } else if (e.key === 'Escape') {
    isOpen.value = false;
  }
}
function select(result: SearchResult) {
  query.value = "";
  isOpen.value = false;
  activeIndex.value = -1;
  emit('select', result);
}
function clear() {
  query.value = '';
  isOpen.value = false;
  activeIndex.value = -1;
  emit('clear');
}
function flatIndex(type: SearchResult['type'], idx: number) {
  let offset = 0;
  for (const g of grouped.value) {
    if (g.type === type) return offset + idx;
    offset += g.items.length;
  }
  return -1;
}
function handleOutsideClick(e: MouseEvent) {
  if (wrapperRef.value && !wrapperRef.value.contains(e.target as Node)) {
    isOpen.value = false;
  }
}
onMounted(() => document.addEventListener('mousedown', handleOutsideClick));
onUnmounted(() => document.removeEventListener('mousedown', handleOutsideClick));
</script>
<template>
  <div ref="wrapperRef" class="search">
    <div
      class="search__box"
      :class="{ 'search__box--open': showDropdown }"
      @click="onBoxClick"
    >
      <span class="search__icon">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
             stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
          <circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/>
        </svg>
      </span>
      <input
        ref="inputRef"
        v-model="query"
        class="search__input"
        :placeholder="placeholder"
        autocomplete="off"
        @input="onInput"
        @focus="onFocus"
        @blur="onBlur"
        @keydown="onKeydown"
      />
      <div class="search__actions">
        <transition name="fade">
          <span v-if="loading || debouncing" class="search__loader" aria-hidden="true">
            <span class="search__loader-dot" />
            <span class="search__loader-dot" />
            <span class="search__loader-dot" />
          </span>
          <button
            v-else-if="query"
            class="search__clear"
            tabindex="-1"
            @mousedown.prevent="clear"
          >
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none"
                 stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
              <path d="M18 6 6 18M6 6l12 12"/>
            </svg>
          </button>
        </transition>
      </div>
    </div>
    <transition name="dropdown">
      <div v-if="showDropdown" class="search__dropdown">
        <div v-if="loading || debouncing" class="search__empty">
          Загрузка...
        </div>
        <template v-else-if="flatResults.length">
          <div v-for="group in grouped" :key="group.type">
            <div class="search__group-label">
              {{ badgeLabels[group.type] }}
            </div>
            <div
              v-for="(item, idx) in group.items"
              :key="item.id"
              class="search__result"
              :class="{ 'search__result--active': flatIndex(group.type, idx) === activeIndex }"
              @mousedown.prevent="select(item)"
              @mouseenter="activeIndex = flatIndex(group.type, idx)"
            >
              <div class="search__thumb">
                <img
                  :src="getImage(item)"
                  :style="{ backgroundImage: `url(${getImage(item)})` }"
                  :alt="item.title"
                />
              </div>
              <div class="search__result-body">
                <span class="search__result-title" v-html="highlight(item.title)" />
                <span v-if="item.id" class="search__result-sub" v-html="'№' + item.id" />
                <span v-if="item.subtitle" class="search__result-sub" v-html="highlight(item.subtitle)" />
              </div>
            </div>
          </div>
        </template>
        <div v-else class="search__empty">Ничего не найдено</div>
        <div class="search__hint">
          Можно ввести номер предзаказа
        </div>
      </div>
    </transition>
  </div>
</template>
<style scoped lang="scss">
.search {
  position: relative;
  font-family: inherit;
  &__box {
    display: flex;
    align-items: center;
    gap: 8px;
    background: #ffffff;
    border: 2px solid #bcbcbc;
    border-radius: 50px;
    padding: 6px 12px;
    height: 100%;
    width: 100%;
    cursor: text;
    transition: border-radius 0.3s ease, border-bottom-color 0.3s ease;
    user-select: none;
    &--open {
      border-radius: 16px 16px 0 0;
      border-bottom-color: transparent;
    }
  }
  &__icon {
    color: #303030;
    display: flex;
    flex-shrink: 0;
  }
  &__input {
    flex: 1;
    border: none;
    outline: none;
    font-size: 14px;
    font-weight: 500;
    font-family: inherit;
    background: transparent;
    color: #303030;
    cursor: text;
    min-width: 0;
    &::placeholder {
      color: rgba(51, 51, 51, 0.9);
      font-weight: 400;
    }
  }
  &__actions {
    width: 20px;
    height: 20px;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    position: relative;
  }
  &__clear {
    display: flex;
    align-items: center;
    justify-content: center;
    background: none;
    border: none;
    cursor: pointer;
    padding: 2px;
    color: rgba(255, 255, 255, 0.7);
    transition: color 0.2s;
    border-radius: 50%;
    width: 20px;
    height: 20px;
    &:hover { color: #303030; }
  }
  &__loader {
    display: flex;
    gap: 3px;
    align-items: center;
  }
  &__loader-dot {
    width: 5px;
    height: 5px;
    border-radius: 50%;
    background: rgba(0, 0, 0, 0.7);
    animation: search-loader 1.4s infinite both;
    &:nth-child(1) { animation-delay: -0.32s; }
    &:nth-child(2) { animation-delay: -0.16s; }
  }
  &__dropdown {
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    background: #fff;
    border: 2px solid #bcbcbc;
    border-top: none;
    border-radius: 0 0 16px 16px;
    overflow: hidden;
    z-index: 100;
  }
  &__group-label {
    padding: 8px 14px 3px;
    font-size: 11px;
    font-weight: 500;
    color: #707070;
    letter-spacing: 0.05em;
    text-transform: uppercase;
    border-top: 1px solid #f0f0f0;
    &:first-child { border-top: none; }
  }
  &__result {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 8px 14px;
    cursor: pointer;
    transition: background 0.12s;
    user-select: none;
    -webkit-tap-highlight-color: transparent;
    &:hover { background: #ededed; }
    &:active { background: #ffffff; }
  }
  &__thumb {
    width: 36px;
    height: 36px;
    border-radius: 8px;
    flex-shrink: 0;
    background: #f5f5f5;
    border: 1px solid #ebebeb;
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
    pointer-events: none;
    img { width: 100%; height: 100%; object-fit: cover; }
  }
  &__result-body {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 1px;
    pointer-events: none;
  }
  &__result-title {
    font-size: 13px;
    font-weight: 500;
    color: #303030;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    :deep(mark) { background: none; font-weight: 700; color: #0c0c0c; }
  }
  &__result-sub {
    font-size: 11px;
    color: #303030;
    :deep(mark) { background: none; font-weight: 600; color: #555; }
  }
  &__badge {
    font-size: 10px;
    font-weight: bold;
    padding: 3px 10px;
    border-radius: 50px;
    border: 2px solid #ffffff;
    white-space: nowrap;
    flex-shrink: 0;
    pointer-events: none;
    background: transparent;
    color: #ffffff;
    &--product {
      background: #ffffff;
      color: #303030;
    }
  }
  &__empty {
    padding: 20px 14px;
    font-size: 13px;
    color: #303030;
    text-align: center;
  }
  &__hint {
    padding: 6px 14px;
    border-top: 1px solid #e3e3e3;
    font-size: 11px;
    color: #303030;
  }
}
.fade-enter-active,
.fade-leave-active { transition: opacity 0.15s; }
.fade-enter-from,
.fade-leave-to { opacity: 0; }
.dropdown-enter-active { transition: opacity 0.15s, transform 0.15s; }
.dropdown-leave-active { transition: opacity 0.1s; }
.dropdown-enter-from   { opacity: 0; transform: translateY(-4px); }
.dropdown-leave-to     { opacity: 0; }
@keyframes search-loader {
  0%, 80%, 100% { transform: scale(0); }
  40% { transform: scale(1); }
}
</style>
